import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
} from "../../../apis/API";
import { lookupWorkflow } from "../workflow/workflowRegistry";

/**
 * Whether the client can render this question:
 * - `bool` / `text` are always supported
 * - `workflow` is supported only when its `workflowName` is in the registry
 * - any other / missing `questionType` is unsupported
 */
const isSupported = (question: LocalizedParticipantQuestion): boolean => {
  switch (question.questionType) {
    case QuestionType.bool:
    case QuestionType.text:
      return true;
    case QuestionType.workflow:
      return lookupWorkflow(question.workflowName) !== null;
    default:
      return false;
  }
};

export interface ClassifiedQuestions {
  /** Questions the participant answers, in display order. */
  supported: LocalizedParticipantQuestion[];
  /** Pre-built `skipped: true` answers for unsupported questions. */
  skipped: IdentifiableAnswerV3Input[];
}

/**
 * Split the assigned questions into supported (rendered/answered in sequence)
 * and skipped (unsupported question type or unknown workflow). Skipped answers
 * are recorded up front — at the engine layer, per the IHA-346 review — and are
 * excluded from navigation/completion. The unknown `workflowName` is not echoed;
 * the skipped answer keeps the server-declared `questionType` (defaulting to
 * `bool` for an unknown type), matching the Android contract.
 */
export const classifyQuestions = (
  questions: (LocalizedParticipantQuestion | null)[] | null | undefined,
): ClassifiedQuestions => {
  const supported: LocalizedParticipantQuestion[] = [];
  const skipped: IdentifiableAnswerV3Input[] = [];

  for (const raw of questions ?? []) {
    if (!raw) {
      continue;
    }
    const question = raw;
    if (isSupported(question)) {
      supported.push(question);
    } else {
      skipped.push({
        questionId: question.id ?? "",
        // Only an unknown-workflow question reaches the skip branch with a known
        // type (bool/text are always supported); any other/missing type defaults
        // to bool. The unknown workflowName is not echoed (Android contract).
        questionType:
          question.questionType === QuestionType.workflow
            ? QuestionType.workflow
            : QuestionType.bool,
        skipped: true,
      });
    }
  }

  return { supported, skipped };
};
